// Frontend/src/lib/clearCaches.ts
//
// Wipe the service worker's response cache on logout.
//
// This app is a PWA, and its workbox config caches every `/api/` GET into a
// single `api-cache` keyed by URL alone — nothing in the key identifies the
// user. Logging out cleared sessionStorage and the React Query cache, but not
// Cache Storage, so the previous user's API responses stayed on the device and
// stayed readable. On a shared machine — a building office tablet, two HOA
// admins on one laptop — that is one person's data sitting in another's
// session.
//
// It is only *served* when the network fails, since the rule is NetworkFirst.
// That makes it unlikely to be seen, not absent: the bytes are on disk either
// way, and "you would need bad wifi to read your colleague's notifications" is
// not a security property.
//
// Notification endpoints are additionally excluded from caching entirely in
// vite.config.ts. This covers everything else — invoices, payments, residents.

/**
 * Delete every Cache Storage entry this origin owns.
 *
 * Never throws and never blocks: logout must complete even if the browser
 * denies cache access (Safari private mode, storage pressure, no SW support).
 * Failing to clear a cache is worth logging, not worth trapping someone in a
 * session they asked to leave.
 */
export async function clearAppCaches(): Promise<void> {
  try {
    if (typeof caches === "undefined") return;
    const names = await caches.keys();
    await Promise.all(names.map((name) => caches.delete(name)));
  } catch (error) {
    console.error("Failed to clear service worker caches on logout:", error);
  }
}

/**
 * Caches written by a build that predates the notifications exclusion.
 *
 * Shipping the fix does not clean up after the bug. An already-installed PWA
 * activates the new service worker and stops *writing* notification responses,
 * but whatever the old one already wrote stays on disk until that user happens
 * to log out — and the people carrying this data are exactly the installed
 * base we are trying to protect.
 *
 * The runtime cache was renamed alongside this, so anything under the old name
 * is by definition from a vulnerable build and safe to drop. After the first
 * boot on a new build this finds nothing and costs one `caches.keys()`.
 */
const LEGACY_CACHE_NAMES = ["api-cache"];

export async function purgeLegacyCaches(): Promise<void> {
  try {
    if (typeof caches === "undefined") return;
    const names = await caches.keys();
    const stale = names.filter((name) => LEGACY_CACHE_NAMES.includes(name));
    if (stale.length === 0) return;
    await Promise.all(stale.map((name) => caches.delete(name)));
    console.info("[pwa] removed cache from a previous build:", stale.join(", "));
  } catch (error) {
    console.error("Failed to purge legacy caches:", error);
  }
}
